import { mdiCamera, mdiUpload } from '@mdi/js'
import Icon from '@mdi/react'
import { getAppConfiguration } from 'AppSettings'
import React, { useState, useEffect } from 'react'
import { Button, Col, Container, Row } from 'react-bootstrap'
import { useWizard } from 'react-use-wizard'
import { getTotalSizeFiles, isFileValid } from 'share/utils/fileUtils'
import { DocumentAddError, DocumentAddStep } from '../model'
import '../styles.scss'

interface StepStartProps {
  subcategoryName: string
  subcategoryDescription: string
  showTotalSizeMessage?: boolean
  files: File[]
  onSelectedFile: (selectedFile: File) => void
  onCancel: () => void
  isAiCase: boolean
}

const StepStart = (props: StepStartProps): JSX.Element => {
  const { nextStep } = useWizard()
  const [error, setError] = useState(DocumentAddError.NoError)
  const [ALLOWED_EXTENSIONS, setALLOWED_EXTENSIONS] = useState<string[]>([])
  const [allowedFilesMessage, setAllowedFilesMessage] = useState('')
  const docsSettings = getAppConfiguration().documents

  useEffect(() => {
    if (props.isAiCase) {
      setALLOWED_EXTENSIONS(
        new Array<string>(
          '.doc',
          '.docx',
          '.rtf',
          '.txt',
          '.pdf',
          '.png',
          '.jpg',
          '.jpeg',
          '.gif',
          '.bmp'
        )
      )
      setAllowedFilesMessage(
        'This file type is not supported. Accepted types are pdf, jpg/jpeg, gif, png, and bmp.'
      )
    } else {
      setALLOWED_EXTENSIONS(
        new Array<string>(
          '.doc',
          '.docx',
          '.rtf',
          '.txt',
          '.pdf',
          '.png',
          '.jpg',
          '.jpeg',
          '.gif',
          '.bmp'
        )
      )
      setAllowedFilesMessage(
        'This file type is not supported. Accepted types are doc, docx, rtf, txt, pdf, jpg/jpeg, gif, png, and bmp.'
      )
    }
  }, [])

  const onSelectedFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(DocumentAddError.NoError)
    const totalSize = getTotalSizeFiles(props.files)

    if (event.target.validity.valid && event.target.files) {
      const file: File = event.target.files?.[0]

      if (!isFileValid(file, ALLOWED_EXTENSIONS)) {
        setError(DocumentAddError.FileNotAllowedError)
        return
      }

      if (file.size > docsSettings.maxFileSize) {
        setError(DocumentAddError.FileSizeError)
      } else if (totalSize + file.size > docsSettings.maxTotalFilesSize) {
        setError(DocumentAddError.TotalSizeError)
      } else {
        props.onSelectedFile(file)
        nextStep(DocumentAddStep.EditFile)
      }
    }
  }

  const onCancel = () => {
    props.files.length
      ? nextStep(DocumentAddStep.SubmitFiles)
      : props.onCancel()
  }

  const getErrorMessage = (): string => {
    switch (error) {
      case DocumentAddError.FileSizeError:
        return 'This file exceeds the maximum file size of 25MB. Please try again.'
      case DocumentAddError.TotalSizeError:
        return 'This file exceeds the maximum upload total size of 95MB. Please try again.'
      case DocumentAddError.MaxFilesError:
        return 'There are 50 files in the list. No more files can be added.'
      case DocumentAddError.FileNotAllowedError:
        return allowedFilesMessage
      default:
        return ''
    }
  }

  return (
    <React.Fragment>
      <Container>
        <Row>
          <Col xs>
            <h2>{props.subcategoryName}</h2>
            <p>{props.subcategoryDescription}</p>
          </Col>
        </Row>
        <Row className='justify-content-md-center'>
          <Col xs md='6'>
            <Button
              variant='outline-secondary'
              className='w-100 d-lg-none btn-custom-secondary'
              onClick={() => nextStep(DocumentAddStep.TakePhoto)}
            >
              <Icon path={mdiCamera} size={1} /> Take a photo
            </Button>
          </Col>
        </Row>
        <Row className='justify-content-md-center mt-3'>
          <Col xs md='6'>
            <label
              htmlFor='upload-file'
              className='btn btn-primary label-input w-100'
              style={{ color: 'white' }}
            >
              <Icon path={mdiUpload} size={1} /> Upload a File
            </label>
            <input
              id='upload-file'
              data-testid='upload-file'
              type='file'
              onChange={onSelectedFile}
              accept={ALLOWED_EXTENSIONS.join()}
            ></input>
          </Col>
        </Row>
        <Row className='mt-3'>
          <Col className='text-md-center'>
            <p>
              <small>
                File size cannot exceed 25 MB.
                {props.showTotalSizeMessage &&
                  'Total upload size cannot exceed 95 MB'}
              </small>
            </p>
            <p
              hidden={error === DocumentAddError.NoError}
              className='text-danger'
            >
              <small>{getErrorMessage()}</small>
            </p>
          </Col>
        </Row>
      </Container>
      <div className='modal-footer modal-footer-custom'>
        <Button
          className='footer-btn btn-custom-secondary'
          variant='outline-secondary'
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </React.Fragment>
  )
}

export default StepStart
